import { App, Input } from 'antd';
import { useLayoutEffect } from 'react';
import type { ReactNode } from 'react';

type AppApi = ReturnType<typeof App.useApp>;

let activeApi: AppApi | null = null;

function bindMethodProxy<T extends object>(key: keyof AppApi) {
  return new Proxy({} as T, {
    get(_target, property) {
      const api = activeApi?.[key] as T | undefined;
      const value = api?.[property as keyof T];
      return typeof value === 'function' ? value.bind(api) : value;
    }
  });
}

export const appMessage = bindMethodProxy<AppApi['message']>('message');
export const appModal = bindMethodProxy<AppApi['modal']>('modal');
export const appNotification = bindMethodProxy<AppApi['notification']>('notification');

export function useAppFeedback() {
  return App.useApp();
}

export function FeedbackBridge() {
  const api = useAppFeedback();
  useLayoutEffect(() => {
    activeApi = api;
    return () => {
      if (activeApi === api) activeApi = null;
    };
  }, [api]);
  return null;
}

export function confirmAction(options: {
  title: ReactNode;
  content?: ReactNode;
  okText?: string;
  cancelText?: string;
  danger?: boolean;
}) {
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    appModal.confirm({
      title: options.title,
      content: options.content,
      okText: options.okText || '确定',
      cancelText: options.cancelText || '取消',
      okButtonProps: options.danger ? { danger: true } : undefined,
      onOk: () => finish(true),
      onCancel: () => finish(false),
      afterClose: () => finish(false)
    });
  });
}

export function confirmPaidContestEntry(options: {
  title: string;
  amount: number;
  okText?: string;
}) {
  return confirmAction({
    title: '付费比赛提醒',
    content: (
      <span>
        进入「{options.title}」需要支付 ¥{Number(options.amount || 0).toFixed(2)}。
        点击继续不会立即扣费，支付前仍需确认扣费明细。
      </span>
    ),
    okText: options.okText || '继续进入',
    cancelText: '取消'
  });
}

export function promptText(options: {
  title: ReactNode;
  label?: ReactNode;
  initialValue?: string;
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  maxLength?: number;
  okText?: string;
}) {
  return new Promise<string | null>((resolve) => {
    let value = options.initialValue || '';
    let settled = false;
    const finish = (result: string | null) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    const Field = options.multiline ? Input.TextArea : Input;
    appModal.confirm({
      title: options.title,
      content: (
        <div className="front-feedback-prompt-v2">
          {options.label ? <span>{options.label}</span> : null}
          <Field
            autoFocus
            defaultValue={value}
            placeholder={options.placeholder}
            maxLength={options.maxLength}
            autoSize={options.multiline ? { minRows: 3, maxRows: 6 } : undefined}
            onChange={(event) => {
              value = event.target.value;
            }}
          />
        </div>
      ),
      okText: options.okText || '确定',
      cancelText: '取消',
      onOk: () => {
        const result = value.trim();
        if (options.required && !result) {
          appMessage.warning('请输入内容');
          return Promise.reject();
        }
        finish(result);
      },
      onCancel: () => finish(null),
      afterClose: () => finish(null)
    });
  });
}

export function confirmDangerAction(options: Omit<Parameters<typeof confirmAction>[0], 'danger'>) {
  return confirmAction({ ...options, danger: true });
}

export function showTextDialog(title: ReactNode, value: string) {
  appModal.info({
    title,
    content: <Input readOnly value={value} onFocus={(event) => event.currentTarget.select()} />,
    okText: '完成'
  });
}
